﻿import { getCountryName, t } from "./locales.js";
import { getSources } from "./sources.js";
import { getCountryPack } from "./countries.js";
import type {
  ComplianceEvaluation,
  ComplianceFinding,
  ComplianceStatus,
  CountryPack,
  CountrySpecificCheckResult,
  InvoiceAddress,
  InvoiceInput,
  LocaleCode,
  PreparedInvoice,
  PreparedInvoiceItem,
  ThresholdAssessment
} from "./types.js";

const roundCurrency = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

const normalizeAddress = (address: InvoiceAddress | undefined): InvoiceAddress | undefined => {
  if (!address) {
    return undefined;
  }
  return {
    line1: address.line1?.trim(),
    line2: address.line2?.trim(),
    city: address.city?.trim(),
    state: address.state?.trim(),
    postalCode: address.postalCode?.trim(),
    countryCode: address.countryCode?.trim().toUpperCase(),
    raw: address.raw?.trim()
  };
};

const addressExists = (address: InvoiceAddress | undefined): boolean =>
  Boolean(address?.raw || address?.line1 || address?.city || address?.postalCode || address?.countryCode);

const getPathValue = (input: Record<string, unknown>, path: string): unknown => {
  return path.split(".").reduce<unknown>((current, segment) => {
    if (current && typeof current === "object" && segment in current) {
      return (current as Record<string, unknown>)[segment];
    }
    return undefined;
  }, input);
};

const fieldExists = (invoice: PreparedInvoice, path: string): boolean => {
  if (path === "items") {
    return invoice.items.length > 0;
  }
  if (path === "seller.address") {
    return addressExists(invoice.seller.address);
  }
  if (path === "buyer.address") {
    return addressExists(invoice.buyer.address);
  }
  const value = getPathValue(invoice as unknown as Record<string, unknown>, path);
  if (typeof value === "number") {
    return Number.isFinite(value);
  }
  if (typeof value === "boolean") {
    return true;
  }
  return typeof value === "string" ? value.trim().length > 0 : Boolean(value);
};

const lineFieldExists = (item: PreparedInvoiceItem, path: string): boolean => {
  const value = getPathValue(item as unknown as Record<string, unknown>, path);
  if (typeof value === "number") {
    return Number.isFinite(value);
  }
  return typeof value === "string" ? value.trim().length > 0 : Boolean(value);
};

const normalizeItems = (items: InvoiceInput["items"]): PreparedInvoiceItem[] => {
  return items.map((item, index) => {
    const quantity = Number(item.quantity ?? 0);
    const unitPrice = Number(item.unitPrice ?? 0);
    const discountAmount = Number(item.discountAmount ?? 0);
    const amountExcludingTax = roundCurrency(Number(item.amountExcludingTax ?? quantity * unitPrice - discountAmount));
    const taxRate = Number(item.taxRate ?? 0);
    const taxAmount = roundCurrency(Number(item.taxAmount ?? (amountExcludingTax * taxRate) / 100));
    const totalAmount = roundCurrency(Number(item.totalAmount ?? amountExcludingTax + taxAmount));

    return {
      id: item.id ?? `line-${index + 1}`,
      description: item.description?.trim() ?? "",
      classificationCode: item.classificationCode?.trim(),
      quantity,
      unitPrice,
      discountAmount,
      amountExcludingTax,
      taxRate,
      taxAmount,
      totalAmount,
      taxType: item.taxType?.trim()
    };
  });
};

export const prepareInvoice = (input: InvoiceInput): PreparedInvoice => {
  const locale = input.locale ?? "en";
  const items = normalizeItems(input.items ?? []);
  const subtotal = roundCurrency(Number(input.totals.subtotal ?? items.reduce((sum, item) => sum + item.amountExcludingTax, 0)));
  const discountTotal = roundCurrency(Number(input.totals.discountTotal ?? items.reduce((sum, item) => sum + item.discountAmount, 0)));
  const taxTotal = roundCurrency(Number(input.totals.taxTotal ?? items.reduce((sum, item) => sum + item.taxAmount, 0)));
  const roundingTotal = roundCurrency(Number(input.totals.roundingTotal ?? 0));
  const prepaidTotal = roundCurrency(Number(input.totals.prepaidTotal ?? 0));
  const grandTotal = roundCurrency(Number(input.totals.grandTotal ?? subtotal + taxTotal + roundingTotal - prepaidTotal));

  return {
    ...input,
    locale,
    issueDate: input.issueDate?.trim(),
    issueTime: input.issueTime?.trim(),
    invoiceType: input.invoiceType?.trim(),
    invoiceNumber: input.invoiceNumber?.trim(),
    buyerType: input.buyerType ?? "business",
    supplyNature: input.supplyNature ?? "domestic",
    businessActivity: input.businessActivity ?? "general_services",
    notes: input.notes ?? [],
    items,
    seller: {
      ...input.seller,
      legalName: input.seller.legalName?.trim(),
      registrationNumber: input.seller.registrationNumber?.trim(),
      taxNumber: input.seller.taxNumber?.trim(),
      address: normalizeAddress(input.seller.address)
    },
    buyer: {
      ...input.buyer,
      legalName: input.buyer.legalName?.trim(),
      registrationNumber: input.buyer.registrationNumber?.trim(),
      taxNumber: input.buyer.taxNumber?.trim(),
      address: normalizeAddress(input.buyer.address)
    },
    totals: {
      subtotal,
      discountTotal,
      taxTotal,
      grandTotal,
      roundingTotal,
      prepaidTotal,
      currency: (input.totals.currency ?? getCountryPack(input.country).localCurrency).toUpperCase(),
      exchangeRateToLocal: Number(input.totals.exchangeRateToLocal ?? 1),
      localSubtotal: input.totals.localSubtotal,
      localTaxTotal: input.totals.localTaxTotal,
      localGrandTotal: input.totals.localGrandTotal
    }
  };
};

const createFinding = (
  invoice: PreparedInvoice,
  ruleId: string,
  severity: ComplianceFinding["severity"],
  riskLevel: ComplianceFinding["riskLevel"],
  titleKey: string,
  summaryKey: string,
  actionKey: string,
  params: Record<string, string | number>,
  sourceIds: string[],
  category: ComplianceFinding["category"],
  fieldPath?: string
): ComplianceFinding => ({
  id: `${ruleId}-${fieldPath ?? "general"}`,
  ruleId,
  category,
  severity,
  riskLevel,
  title: t(invoice.locale, titleKey, params),
  summary: t(invoice.locale, summaryKey, params),
  action: t(invoice.locale, actionKey, params),
  fieldPath,
  sourceIds
});

const currencyFormatter = (value: number, currency: string): string =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value) + ` ${currency}`;

const thresholdTaxLabel = (pack: CountryPack, future = false): string => {
  if (pack.code === "SG") {
    return "GST";
  }
  if (pack.code === "MY") {
    return future ? "SST / e-Invoice" : "SST";
  }
  if (pack.code === "TH") {
    return "VAT";
  }
  return "PPN / PKP";
};

const buildThresholds = (invoice: PreparedInvoice, pack: CountryPack): ThresholdAssessment[] => {
  return pack.thresholdRules.map((rule) => {
    if (!rule.businessActivities.includes(invoice.businessActivity ?? "other")) {
      return {
        id: rule.id,
        title: rule.title[invoice.locale],
        message: t(invoice.locale, "threshold_status_na"),
        status: "not_applicable",
        threshold: rule.threshold,
        currency: rule.currency,
        sourceIds: rule.sourceIds
      };
    }

    const current = invoice.turnoverLast12Months;
    const projected = invoice.projectedTurnoverNext12Months;
    const thresholdText = currencyFormatter(rule.threshold, rule.currency);
    const currentText = current === undefined ? "-" : currencyFormatter(current, rule.currency);
    const projectedText = projected === undefined ? "-" : currencyFormatter(projected, rule.currency);

    if (rule.currentLogic === "country_schedule") {
      const above = (invoice.turnoverLast12Months ?? 0) >= 1_000_000;
      return {
        id: rule.id,
        title: rule.title[invoice.locale],
        message: above
          ? t(invoice.locale, "threshold_above_summary", {
              taxLabel: "e-Invoice",
              threshold: thresholdText,
              current: currentText
            })
          : t(invoice.locale, "threshold_status_below"),
        status: above ? "above_threshold" : "below_threshold",
        threshold: rule.threshold,
        currency: rule.currency,
        currentTurnover: current,
        projectedTurnover: projected,
        sourceIds: rule.sourceIds
      };
    }

    if (current !== undefined && current >= rule.threshold) {
      return {
        id: rule.id,
        title: rule.title[invoice.locale],
        message: t(invoice.locale, "threshold_above_summary", {
          taxLabel: thresholdTaxLabel(pack),
          threshold: thresholdText,
          current: currentText
        }),
        status: "above_threshold",
        threshold: rule.threshold,
        currency: rule.currency,
        currentTurnover: current,
        projectedTurnover: projected,
        rate: rule.rate,
        sourceIds: rule.sourceIds
      };
    }

    if (projected !== undefined && projected >= rule.threshold) {
      return {
        id: rule.id,
        title: rule.title[invoice.locale],
        message: t(invoice.locale, "threshold_future_summary", {
          taxLabel: thresholdTaxLabel(pack, true),
          threshold: thresholdText,
          projected: projectedText
        }),
        status: "future_liability",
        threshold: rule.threshold,
        currency: rule.currency,
        currentTurnover: current,
        projectedTurnover: projected,
        rate: rule.rate,
        sourceIds: rule.sourceIds
      };
    }

    const nearThreshold = current !== undefined && current >= rule.threshold * 0.8;
    return {
      id: rule.id,
      title: rule.title[invoice.locale],
      message: nearThreshold
        ? t(invoice.locale, "threshold_near_summary", {
            taxLabel: thresholdTaxLabel(pack, true),
            threshold: thresholdText,
            current: currentText
          })
        : t(invoice.locale, "threshold_status_below"),
      status: nearThreshold ? "near_threshold" : "below_threshold",
      threshold: rule.threshold,
      currency: rule.currency,
      currentTurnover: current,
      projectedTurnover: projected,
      rate: rule.rate,
      sourceIds: rule.sourceIds
    };
  });
};

const crossBorderEvidenceNeeded = (check: CountrySpecificCheckResult): boolean => check.messageKey === "cross-border-evidence";
const applyCountryCheck = (invoice: PreparedInvoice, check: CountrySpecificCheckResult): ComplianceFinding => {
  const countryLabel = getCountryName(invoice.locale, invoice.country);
  const params = {
    country: countryLabel,
    field:
      check.fieldPath === "seller.registrationNumber"
        ? t(invoice.locale, "field_sellerRegistrationNumber")
        : check.fieldPath === "seller.taxNumber"
          ? t(invoice.locale, "field_sellerTaxNumber")
          : check.fieldPath === "buyer.taxNumber"
          ? t(invoice.locale, "field_buyerTaxNumber")
          : check.fieldPath === "issueTime"
              ? t(invoice.locale, "field_issueTime")
              : check.fieldPath === "countryExtensions.transactionCode"
                ? t(invoice.locale, "field_transactionCode")
                : check.fieldPath === "countryExtensions.etaxRegistered"
                  ? t(invoice.locale, "field_etaxRegistered")
                  : check.fieldPath === "countryExtensions.foreignCurrencyApproval"
                    ? t(invoice.locale, "field_foreignCurrencyApproval")
              : check.fieldPath === "notes"
                ? t(invoice.locale, "field_priceIncludesTaxNote")
                : check.fieldPath ?? ""
  };

  if (check.messageKey === "simplified-price-note") {
    return createFinding(
      invoice,
      "sg-simplified-price-note",
      check.severity,
      check.riskLevel,
      "required_field_title",
      "required_field_summary",
      "required_field_action",
      { field: t(invoice.locale, "field_priceIncludesTaxNote") },
      check.sourceIds,
      "country_specific",
      check.fieldPath
    );
  }

  if (check.messageKey === "my-issue-time") {
    return createFinding(
      invoice,
      "my-issue-time",
      check.severity,
      check.riskLevel,
      "required_field_title",
      "required_field_summary",
      "required_field_action",
      { field: t(invoice.locale, "field_issueTime") },
      check.sourceIds,
      "country_specific",
      check.fieldPath
    );
  }

  if (check.messageKey === "my-it-outside-malaysia") {
    return createFinding(
      invoice,
      "my-it-cross-border-note",
      check.severity,
      check.riskLevel,
      "note_title",
      "note_summary",
      "note_action",
      {
        message:
          invoice.locale === "zh-CN"
            ? "IT 服务如果与马来西亚境外的货物、土地或相关事项有关，可能不在服务税征收范围内。"
            : invoice.locale === "ms-MY"
              ? "Perkhidmatan IT yang berkaitan dengan barangan, tanah, atau perkara di luar Malaysia mungkin berada di luar skop cukai perkhidmatan."
              : invoice.locale === "id-ID"
                ? "Layanan TI yang terkait barang, tanah, atau perkara di luar Malaysia dapat berada di luar cakupan pajak layanan."
                : "IT services connected with goods, land, or matters outside Malaysia can fall outside the service-tax scope."
      },
      check.sourceIds,
      "cross_border",
      check.fieldPath
    );
  }

  if (check.messageKey === "th-etax-approval") {
    return createFinding(
      invoice,
      "th-etax-approval",
      check.severity,
      check.riskLevel,
      "required_field_title",
      "required_field_summary",
      "required_field_action",
      { field: t(invoice.locale, "field_etaxRegistered") },
      check.sourceIds,
      "country_specific",
      check.fieldPath
    );
  }

  if (check.messageKey === "th-foreign-approval") {
    return createFinding(
      invoice,
      "th-foreign-currency-approval",
      check.severity,
      check.riskLevel,
      "note_title",
      "note_summary",
      "note_action",
      {
        message:
          invoice.locale === "zh-CN"
            ? "泰国税务发票若使用外币或外文，应事先取得主管机关批准。"
            : invoice.locale === "ms-MY"
              ? "Invois cukai Thailand yang menggunakan mata wang atau bahasa asing memerlukan kelulusan terlebih dahulu."
              : invoice.locale === "id-ID"
                ? "Invoice pajak Thailand yang memakai mata uang atau bahasa asing memerlukan persetujuan terlebih dahulu."
                : invoice.locale === "th-TH"
                  ? "ใบกำกับภาษีไทยที่ใช้สกุลเงินหรือภาษาต่างประเทศควรได้รับอนุมัติล่วงหน้า"
                  : "Thai tax invoices using foreign currency or foreign language should have prior approval."
      },
      check.sourceIds,
      "country_specific",
      check.fieldPath
    );
  }

  if (check.messageKey === "th-abbreviated-retail") {
    return createFinding(
      invoice,
      "th-abbreviated-retail",
      check.severity,
      check.riskLevel,
      "note_title",
      "note_summary",
      "note_action",
      {
        message:
          invoice.locale === "zh-CN"
            ? "泰国简式税务发票通常更适合零售或面对大量终端消费者的场景。"
            : invoice.locale === "ms-MY"
              ? "Invois cukai ringkas Thailand lazimnya lebih sesuai untuk jualan runcit atau pelanggan akhir yang ramai."
              : invoice.locale === "id-ID"
                ? "Invoice pajak ringkas Thailand biasanya lebih cocok untuk penjualan ritel atau pelanggan akhir dalam jumlah banyak."
                : invoice.locale === "th-TH"
                  ? "ใบกำกับภาษีอย่างย่อของไทยมักเหมาะกับการขายปลีกหรือการขายตรงให้ลูกค้าจำนวนมาก"
                  : "Thailand abbreviated tax invoices usually fit retail or direct-to-many-customer scenarios best."
      },
      check.sourceIds,
      "country_specific",
      check.fieldPath
    );
  }

  if (crossBorderEvidenceNeeded(check)) {
    return createFinding(
      invoice,
      `${invoice.country.toLowerCase()}-cross-border`,
      check.severity,
      check.riskLevel,
      "cross_border_title",
      "cross_border_summary",
      "cross_border_action",
      {},
      check.sourceIds,
      "cross_border",
      check.fieldPath
    );
  }

  return createFinding(
    invoice,
    `${invoice.country.toLowerCase()}-format`,
    check.severity,
    check.riskLevel,
    "format_title",
    "format_summary",
    "format_action",
    params,
    check.sourceIds,
    "country_specific",
    check.fieldPath
  );
};

const expectedTaxRate = (invoice: PreparedInvoice, pack: CountryPack): number => {
  const profile = pack.resolveProfile(invoice);
  if (!invoice.taxRegistered) {
    return 0;
  }
  if (invoice.supplyNature === "export_goods" || invoice.supplyNature === "export_services" || invoice.supplyNature === "exempt") {
    return 0;
  }
  return profile.allowedTaxRates.find((rate) => rate > 0) ?? 0;
};

const getOverallRiskLevel = (findings: ComplianceFinding[]): ComplianceFinding["riskLevel"] => {
  if (findings.some((item) => item.riskLevel === "high")) {
    return "high";
  }
  if (findings.some((item) => item.riskLevel === "medium")) {
    return "medium";
  }
  return "low";
};

const buildSummary = (
  invoice: PreparedInvoice,
  status: ComplianceStatus,
  findings: ComplianceFinding[],
  thresholds: ThresholdAssessment[],
  pack: CountryPack
): ComplianceEvaluation["summary"] => {
  const recommendedActions = new Set<string>();
  if (findings.some((item) => item.ruleId.includes("required"))) {
    recommendedActions.add(t(invoice.locale, "action_add_missing_fields"));
  }
  if (findings.some((item) => item.ruleId.includes("tax"))) {
    recommendedActions.add(t(invoice.locale, "action_fix_tax_logic"));
  }
  if (findings.some((item) => item.ruleId.includes("total"))) {
    recommendedActions.add(t(invoice.locale, "action_fix_totals"));
  }
  if (findings.some((item) => item.category === "cross_border")) {
    recommendedActions.add(t(invoice.locale, "action_validate_cross_border"));
  }
  if (thresholds.some((item) => item.status === "near_threshold" || item.status === "above_threshold" || item.status === "future_liability")) {
    recommendedActions.add(t(invoice.locale, "action_check_thresholds"));
  }

  return {
    title: t(invoice.locale, `summary_${status}_title`),
    subtitle: t(invoice.locale, `summary_${status}_subtitle`),
    recommendedActions: [...recommendedActions],
    highlightSourceIds: pack.sourceIds
  };
};

export const evaluateInvoice = (
  input: InvoiceInput,
  options: {
    locale?: LocaleCode;
    existingInvoiceNumbers?: string[];
  } = {}
): ComplianceEvaluation => {
  const invoice = prepareInvoice({ ...input, locale: options.locale ?? input.locale ?? "en" });
  const pack = getCountryPack(invoice.country);
  const profile = pack.resolveProfile(invoice);
  const findings: ComplianceFinding[] = [];

  const requiredFieldLabels: Record<string, string> = {
    invoiceNumber: t(invoice.locale, "field_invoiceNumber"),
    issueDate: t(invoice.locale, "field_issueDate"),
    issueTime: t(invoice.locale, "field_issueTime"),
    "totals.currency": t(invoice.locale, "field_currency"),
    "seller.legalName": t(invoice.locale, "field_sellerLegalName"),
    "seller.registrationNumber": t(invoice.locale, "field_sellerRegistrationNumber"),
    "seller.taxNumber": t(invoice.locale, "field_sellerTaxNumber"),
    "seller.address": t(invoice.locale, "field_sellerAddress"),
    "buyer.legalName": t(invoice.locale, "field_buyerLegalName"),
    "buyer.address": t(invoice.locale, "field_buyerAddress"),
    "buyer.taxNumber": t(invoice.locale, "field_buyerTaxNumber"),
    "buyer.phone": t(invoice.locale, "field_buyerPhone"),
    "countryExtensions.transactionCode": t(invoice.locale, "field_transactionCode")
  };

  for (const fieldPath of profile.requiredFields) {
    if (!fieldExists(invoice, fieldPath)) {
      findings.push(
        createFinding(
          invoice,
          `required-${fieldPath}`,
          "error",
          "high",
          "required_field_title",
          "required_field_summary",
          "required_field_action",
          { field: requiredFieldLabels[fieldPath] ?? fieldPath },
          profile.sourceIds,
          "identity",
          fieldPath
        )
      );
    }
  }

  invoice.items.forEach((item, index) => {
    profile.requiredLineFields.forEach((fieldPath) => {
      if (!lineFieldExists(item, fieldPath)) {
        findings.push(
          createFinding(
            invoice,
            `required-line-${fieldPath}-${index}`,
            "error",
            "high",
            "required_field_title",
            "required_field_summary",
            "required_field_action",
            {
              field:
                fieldPath === "description"
                  ? t(invoice.locale, "field_itemDescription")
                  : fieldPath === "classificationCode"
                    ? t(invoice.locale, "field_itemClassificationCode")
                    : fieldPath === "quantity"
                      ? t(invoice.locale, "field_itemQuantity")
                      : fieldPath === "unitPrice"
                        ? t(invoice.locale, "field_itemUnitPrice")
                        : fieldPath
            },
            profile.sourceIds,
            "amount",
            `items[${index}].${fieldPath}`
          )
        );
      }
    });
  });

  const subtotalFromLines = roundCurrency(invoice.items.reduce((sum, item) => sum + item.amountExcludingTax, 0));
  const taxFromLines = roundCurrency(invoice.items.reduce((sum, item) => sum + item.taxAmount, 0));
  const grandFromLines = roundCurrency(subtotalFromLines + taxFromLines + invoice.totals.roundingTotal - invoice.totals.prepaidTotal);

  if (
    roundCurrency(Math.abs(invoice.totals.subtotal - subtotalFromLines)) > 0.01 ||
    roundCurrency(Math.abs(invoice.totals.taxTotal - taxFromLines)) > 0.01 ||
    roundCurrency(Math.abs(invoice.totals.grandTotal - grandFromLines)) > 0.01
  ) {
    findings.push(
      createFinding(
        invoice,
        "total-mismatch",
        "error",
        "high",
        "total_mismatch_title",
        "total_mismatch_summary",
        "total_mismatch_action",
        {
          difference: currencyFormatter(roundCurrency(Math.abs(invoice.totals.grandTotal - grandFromLines)), invoice.totals.currency)
        },
        profile.sourceIds,
        "amount"
      )
    );
  }

  const invoiceExpectedRate = expectedTaxRate(invoice, pack);
  const lineRates = [...new Set(invoice.items.map((item) => item.taxRate))];
  const invalidRate = lineRates.find((rate) => !profile.allowedTaxRates.includes(rate));
  if (invalidRate !== undefined) {
    findings.push(
      createFinding(
        invoice,
        "tax-rate-mismatch",
        "error",
        "high",
        "tax_rate_title",
        "tax_rate_summary",
        "tax_rate_action",
        {
          expectedRate: invoiceExpectedRate,
          actualRate: invalidRate
        },
        profile.sourceIds,
        "tax"
      )
    );
  }

  if (invoice.taxRegistered && !invoice.seller.taxNumber) {
    findings.push(
      createFinding(
        invoice,
        "tax-id-required",
        "error",
        "high",
        "tax_id_required_title",
        "tax_id_required_summary",
        "tax_id_required_action",
        {
          taxLabel: invoice.country === "SG" ? "GST" : invoice.country === "MY" ? "SST / e-Invoice" : "PPN"
        },
        profile.sourceIds,
        "tax",
        "seller.taxNumber"
      )
    );
  }

  if (!invoice.taxRegistered && invoice.totals.taxTotal > 0) {
    findings.push(
      createFinding(
        invoice,
        "unregistered-tax-charged",
        "error",
        "high",
        "tax_rate_title",
        "tax_rate_summary",
        "tax_rate_action",
        {
          expectedRate: 0,
          actualRate: lineRates[0] ?? 0
        },
        profile.sourceIds,
        "tax"
      )
    );
  }

  if (invoice.invoiceNumber && options.existingInvoiceNumbers?.includes(invoice.invoiceNumber)) {
    findings.push(
      createFinding(
        invoice,
        "duplicate-invoice",
        "error",
        "high",
        "duplicate_title",
        "duplicate_summary",
        "duplicate_action",
        { invoiceNumber: invoice.invoiceNumber },
        profile.sourceIds,
        "integrity"
      )
    );
  }

  const countryChecks = pack.extraChecks(invoice);
  countryChecks.forEach((check) => findings.push(applyCountryCheck(invoice, check)));

  const thresholds = buildThresholds(invoice, pack);
  thresholds
    .filter((item) => item.status === "near_threshold" || item.status === "above_threshold" || item.status === "future_liability")
    .forEach((threshold) => {
      findings.push(
        createFinding(
          invoice,
          threshold.id,
          threshold.status === "above_threshold" ? "warning" : "info",
          threshold.status === "above_threshold" ? "medium" : "low",
          threshold.status === "future_liability"
            ? "threshold_future_title"
            : threshold.status === "above_threshold"
              ? "threshold_above_title"
              : "threshold_near_title",
          threshold.status === "future_liability"
            ? "threshold_future_summary"
            : threshold.status === "above_threshold"
              ? "threshold_above_summary"
              : "threshold_near_summary",
          threshold.status === "future_liability"
            ? "threshold_future_action"
            : threshold.status === "above_threshold"
              ? "threshold_above_action"
              : "threshold_near_action",
          {
            taxLabel: invoice.country === "SG" ? "GST" : invoice.country === "MY" ? "SST / e-Invoice" : "PPN / PKP",
            threshold: currencyFormatter(threshold.threshold, threshold.currency),
            current: threshold.currentTurnover === undefined ? "-" : currencyFormatter(threshold.currentTurnover, threshold.currency),
            projected:
              threshold.projectedTurnover === undefined ? "-" : currencyFormatter(threshold.projectedTurnover, threshold.currency)
          },
          threshold.sourceIds,
          "threshold"
        )
      );
    });

  const status: ComplianceStatus = findings.some((item) => item.severity === "error")
    ? "fail"
    : findings.some((item) => item.severity === "warning")
      ? "review"
      : "pass";

  const sourceIds = [...new Set(findings.flatMap((item) => item.sourceIds).concat(pack.sourceIds))];

  return {
    status,
    overallRiskLevel: getOverallRiskLevel(findings),
    country: invoice.country,
    locale: invoice.locale,
    invoiceProfile: t(invoice.locale, profile.key),
    rulebookVersion: pack.rulebookVersion,
    evaluatedAt: new Date().toISOString(),
    normalizedInvoice: invoice,
    findings,
    thresholds,
    summary: buildSummary(invoice, status, findings, thresholds, pack),
    sourceIds: getSources(sourceIds).map((source) => source.id)
  };
};

export const getRuleSources = (input: InvoiceInput): ReturnType<typeof getSources> => {
  const pack = getCountryPack(input.country);
  return getSources(pack.sourceIds);
};


